import type { ObjectId } from 'bson';
import type { Document, LeanDocument } from 'mongoose';
import type { NetworkDiagram } from '../templates';

export enum EventType {
  PAUSE = 'pause',
  UNPAUSE = 'unpause',
  DEPLOY = 'deploy',
  RESET = 'reset',
  DESTROY = 'destroy',
  ADD_USER = 'add_user',
  REMOVE_USER = 'remove_user',
}

export enum EventActor {
  USER = 'user',
  SYSTEM = 'system',
}

export interface EventDetail {
  actor: EventActor;
  networkInstanceId: ObjectId;
  userId?: ObjectId;
  isReset?: boolean;
}
export interface SubnetOutput {
  name: string;
  id: string;
  arn: string;
  urn: string;
  cidrBlock: string;
  availabilityZone: string;
  availabilityZoneId: string;
  vpcId: string;
  tags?: Record<string, string>;
}

export interface VpcOutput {
  id: string;
  arn: string;
  urn: string;
  cidrBlock: string;
  defaultNetworkAclId: string;
  defaultRouteTableId: string;
  defaultSecurityGroupId: string;
  tags?: Record<string, string>;
}

export interface SecurityGroupOutput {
  id: string;
  arn: string;
  urn: string;
  vpcId: string;
  name: string;
  ingress: unknown;
  egress: unknown;
  tags?: Record<string, string>;
}

export interface MachineOutput {
  id: string;
  arn: string;
  urn: string;
  amiId: string;
  instanceType: string;
  publicIp: string;
  privateIp: string;
  subnetId: string;
  vpcId: string;
  elasticIp?: string;
  tags?: Record<string, string>;
}

export interface NetworkInstanceOutput {
  vpc: VpcOutput;
  subnets: SubnetOutput[];
  machines: MachineOutput[];
  vpn: MachineOutput;
  securityGroups: SecurityGroupOutput[];
}

export interface NetworkEventDocument {
  event: string;
  networkInstanceId: ObjectId;
  startTime: Date;
  endTime?: Date;
  summary?: string;
  log?: string;
  resources?: object;
  outputs?: unknown;
  error?: unknown;
  actor: EventActor;
  userId?: ObjectId;
}

export enum NetworkInstanceStatus {
  DEPLOYING = 'deploying',
  RUNNING = 'running',
  ERROR = 'error',
  DELETING = 'deleting',
  DELETED = 'deleted',
  PAUSED = 'paused',
}

export interface PulumiResponse {
  summary: object;
  outputs?: {
    machines: {
      value: object;
    };
    securityGroups: {
      value: object;
    };
    subnets: {
      value: object;
    };
    vpc: {
      value: object;
    };
    vpn: {
      value: object;
    };
  };
}
export interface NetworkInstance {
  status: NetworkInstanceStatus;
  templateId: ObjectId;
  users: { userId: ObjectId; resets?: Date[] }[];
  createdAt: Date;
  vpnServerIp?: string;
  pulumiResponse?: PulumiResponse;
  resources?: object;
  private: boolean;
  isLocked: boolean;
  startTime?: Date;
  pauseTime?: Date;
  resetTime?: Date;
  networkDiagram: NetworkDiagram;
  roomId?: ObjectId;
  events?: NetworkEventDocument[];
}

export type NetworkInstanceDocument = Document<unknown, unknown, NetworkInstance> &
  Omit<
    NetworkInstance & {
      _id: ObjectId;
    },
    never
  >;

export type LeanNetworkInstanceDocument = LeanDocument<
  NetworkInstance & {
    _id: ObjectId;
  }
>;

export interface UserNetworkInstance extends Omit<NetworkInstance, 'users' | 'private' | 'createdAt'> {
  resetsRequested: number;
  resetsRequired: number;
}

export interface AdminNetworkInstance extends Omit<NetworkInstance, 'users' | 'createdAt'> {
  resetsRequested: number;
  resetsRequired: number;
  users: { userId: ObjectId; resets?: Date[]; username: string; avatar: string; level: number }[];
}
